import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { useAuth, dashboardFor } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle, CalendarClock, Sparkles } from "lucide-react";
import { trackPurchase } from "@/lib/metaPixel";
import { trackPayment } from "@/lib/gtm";

export default function CheckoutCallback() {
  const { user, refresh } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "verifying", voucher: null, trialEnd: null, message: "" });
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const reference = params.get("reference") || params.get("trxref");
    if (!reference) {
      setState({ status: "error", message: "Missing payment reference." });
      return;
    }
    api
      .get(`/checkout/verify/${encodeURIComponent(reference)}`)
      .then(async ({ data }) => {
        if (data.status !== "success") {
          setState({ status: "error", message: "Payment was not successful. You have not been charged." });
          return;
        }
        trackPurchase({ currency: "ZAR", content_name: data.voucher_code || data.plan_id || "checkout" });
        trackPayment({ redirect: data.redirect, reference, plan: data.voucher_code || data.plan_id, flow: "checkout" });
        const fresh = await refresh();
        setState({
          status: "ok",
          voucher: data.voucher_code || null,
          trialEnd: data.trial_end_date || null,
          message: "",
        });
        // VILLAGEVIP users stay on this screen to read the "first charge in 90
        // days" message before continuing. Everyone else bounces to their
        // dashboard automatically.
        if (data.voucher_code !== "VILLAGEVIP") {
          setTimeout(() => {
            // Prefer the backend-provided role-based redirect (set from
            // user.role on the verify endpoint). Fall back to the shared
            // dashboardFor helper, then to the legacy vendor override.
            if (data.redirect) {
              window.location.href = data.redirect;
              return;
            }
            if (fresh?.role === "vendor") {
              window.location.href = "/vendor-portal";
              return;
            }
            window.location.href = dashboardFor(fresh || { role: "parent", subscription_status: "active" });
          }, 1500);
        }
      })
      .catch(async (e) => {
        // Paystack sometimes reports "already in place" / "already exists"
        // on verify when the customer actually paid and is subscribed on
        // their side (we have a backend recovery for vendor, but surface
        // the same safety net on the client in case any residual response
        // still trips the error path). If the user's refreshed auth
        // confirms an active subscription, route silently to the dashboard
        // instead of showing an error.
        const detail = (errMsg(e, "") || "").toLowerCase();
        const isDuplicateMsg =
          detail.includes("already in place") ||
          detail.includes("already exists") ||
          detail.includes("already subscribed");
        try {
          const fresh = await refresh();
          if (isDuplicateMsg || fresh?.subscription_status === "active") {
            // Vendors go straight to /vendor-portal on duplicate-recovery
            // too — never bounce them back to a payment page.
            if (fresh?.role === "vendor") {
              window.location.href = "/vendor-portal";
              return;
            }
            window.location.href = dashboardFor(fresh || { role: "parent", subscription_status: "active" });
            return;
          }
        } catch { /* ignore — fall through to the real error */ }
        setState({ status: "error", message: errMsg(e, "Could not verify payment") });
      });
  }, [params, refresh]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 grain" data-testid="checkout-callback">
      <div className="bg-white/80 border border-sage/20 rounded-3xl p-10 max-w-md w-full text-center shadow-sm">
        {state.status === "verifying" && (
          <>
            <Loader2 className="w-12 h-12 text-sage animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-black text-forest mb-2" data-testid="verifying-title">
              Confirming your checkout…
            </h1>
            <p className="text-forest/70 text-sm">This takes just a moment.</p>
          </>
        )}
        {state.status === "ok" && state.voucher === "VILLAGEVIP" && (
          <>
            <span className="inline-flex w-16 h-16 rounded-full bg-sage-light items-center justify-center mb-4">
              <CalendarClock className="w-8 h-8 text-sage-dark" />
            </span>
            <h1 className="text-2xl font-black text-forest mb-2">VILLAGEVIP activated!</h1>
            <p className="text-forest/70 text-sm mb-4" data-testid="villagevip-success-msg">
              Your card is on file. You won't be charged for 90 days — the first R149 payment runs on{" "}
              <strong>{state.trialEnd ? new Date(state.trialEnd).toLocaleDateString() : "the trial end date"}</strong>.
            </p>
            <Button
              className="rounded-full bg-sage hover:bg-sage-dark text-white"
              onClick={() => navigate(user?.role === "vendor" || !user ? "/vendor-portal" : dashboardFor(user))}
              data-testid="villagevip-continue-btn"
            >
              Continue to {user?.role === "event_creator" ? "Event Portal" : user?.role === "marketplace_user" ? "Marketplace Portal" : "vendor dashboard"}
            </Button>
          </>
        )}
        {state.status === "ok" && state.voucher !== "VILLAGEVIP" && (
          <>
            <span className="inline-flex w-16 h-16 rounded-full bg-sage-light items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-sage-dark" />
            </span>
            <h1 className="text-2xl font-black text-forest mb-2">You're in!</h1>
            <p className="text-forest/70 text-sm" data-testid="checkout-success-msg">
              {state.voucher ? (
                <>Voucher <strong>{state.voucher}</strong> applied. Redirecting…</>
              ) : (
                "Redirecting to your dashboard…"
              )}
            </p>
          </>
        )}
        {state.status === "error" && (
          <>
            <span className="inline-flex w-16 h-16 rounded-full bg-terracotta-light items-center justify-center mb-4">
              <XCircle className="w-8 h-8 text-terracotta-dark" />
            </span>
            <h1 className="text-2xl font-black text-forest mb-2">Payment could not be verified</h1>
            <p className="text-forest/70 text-sm mb-6" data-testid="checkout-error-msg">{state.message}</p>
            <Button
              className="rounded-full bg-sage hover:bg-sage-dark text-white"
              onClick={() => navigate("/checkout")}
              data-testid="checkout-retry-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" /> Back to checkout
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
